import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["questionKey"];
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamTerm, useQueryParamsEffect } from 'pricing-pages-ui/utils/urls/appQueryParamUtils';
import { useEffect, useState } from 'react';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { FaqsConfig } from '../../constants/faqs/FaqsConfig';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import Screens from '../../constants/routing/Screens';
import { tabIdToProductLevels } from '../../constants/routing/Tabs';
import { ProductPageContext } from '../../context/ProductPageContext';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { usePageParams } from '../../hooks/usePageParams';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { useAppSelector } from '../../reducers';
import { hasPricingAndPurchasingFailedSelector } from '../../selectors/LoadingStateSelectors';
import { navItemForProductLineSelector } from '../../selectors/RoutingSelectors';
import BannerSection from '../banners/BannerSection';
import CustomerSupport from '../common/CustomerSupport';
import IntegrationsSection from '../common/Integrations';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import Faq from '../faqs/Faq';
import FooterMicrocopy from '../faqs/FooterMicrocopy';
import Question from '../faqs/Question';
import { StyledCardGrid, borderStyle } from '../merch-card/MerchCardSharedComponents';
import ProductPageSubtitle from '../product-pages/ProductPageSubtitle';
import ProductPageTitle, { ProductPageHeader } from '../product-pages/ProductPageTitle';
import MobileHighlightedFreeCard from './MobileHighlightedFreeCard';
import MobileProductLevelSelectorButtons from './MobileProductLevelSelectorButtons';
import MobileFeatureTable from './components/MobileFeatureTable';
import MobileHighlightedMerchCard from './components/MobileHighlightedMerchCard';
import MobileWrapperWithTabs from './containers/MobileWrapperWithTabs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MobileProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const hasPricingAndPurchasingFailed = useAppSelector(hasPricingAndPurchasingFailedSelector);
  const navItem = useAppSelector(state => navItemForProductLineSelector(state, productLine));
  const faqsConfig = FaqsConfig[navItem];
  const screen = Screens[productLine];
  const {
    tab
  } = usePageParams();
  const [selectedTab, setSelectedTab] = useState(tab);
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen,
      tab
    });
    setSelectedTab(tab);
  }, [screen, tab]);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const [selectedProductLevel, setSelectedProductLevel] = useState(productLevels[0]);
  const filteredHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels: [selectedProductLevel],
    productLine
  });
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  const commonStyles = {
    gridColumnStart: 1,
    paddingLeft: 28,
    paddingRight: 28,
    borderRight: borderStyle,
    borderLeft: borderStyle
  };
  const onTabChange = newTab => {
    setSelectedTab(newTab);
    setSelectedProductLevel(tabIdToProductLevels[newTab][0]);
  };
  return /*#__PURE__*/_jsx(MobileWrapperWithTabs, {
    navMarkerName: `${productLine}_PAGE`,
    navItem: navItem,
    tab: selectedTab,
    setTab: onTabChange,
    children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
      value: {
        highlightedProducts: filteredHighlightedProducts
      },
      children: [/*#__PURE__*/_jsx(BannerSection, {
        productLine: productLine,
        screen: screen
      }), /*#__PURE__*/_jsx(FailedLoadBanner, {
        hasFailed: hasPricingAndPurchasingFailed
      }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
        children: [/*#__PURE__*/_jsx(ProductPageTitle, {
          productLine: productLine
        }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
          productLine: productLine
        })]
      }), /*#__PURE__*/_jsx(MobileProductLevelSelectorButtons, {
        productLevels: productLevels,
        onClick: setSelectedProductLevel,
        selectedProductLevel: selectedProductLevel
      }), /*#__PURE__*/_jsx("div", {
        className: "flex-column align-center justify-center",
        children: /*#__PURE__*/_jsx(StyledCardGrid, {
          columns: 1,
          children: selectedProductLevel === 'FREE' ? /*#__PURE__*/_jsx(MobileHighlightedFreeCard, {
            productLine: productLine,
            commonStyles: commonStyles,
            perMonth: false
          }) : /*#__PURE__*/_jsx(MobileHighlightedMerchCard, {
            productLevel: selectedProductLevel,
            productLine: productLine,
            selectedTab: selectedTab,
            commonStyles: commonStyles
          })
        })
      }), /*#__PURE__*/_jsx(MobileFeatureTable, {
        productLine: productLine,
        highlightedProducts: filteredHighlightedProducts
      }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CustomerSupport, {}), /*#__PURE__*/_jsxs(Faq, {
        children: [faqsConfig.map(_ref => {
          let {
              questionKey
            } = _ref,
            passthroughOptions = _objectWithoutPropertiesLoose(_ref, _excluded);
          return /*#__PURE__*/_jsx(Question, Object.assign({
            questionKey: questionKey
          }, passthroughOptions), questionKey);
        }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
      }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
        phoneSourceId: `${productLine.toLowerCase()}-footer`,
        screen: screen
      })]
    })
  });
};
export default MobileProductPage;